import { GameData, select } from "gamedata/GameData";
import invariant from "tiny-invariant";
import { Realm, tt, useT, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE } from "~/util/Loaders";
import { Table } from "~/components/ui/Table";
import { Link } from "react-router";
import { QuestRewardList } from "~/components/quest/QuestRewardList";
import { QuestRaceList } from "~/components/quest/QuestRaceList";
import { QuestProfList } from "~/components/quest/QuestProfList";
import { QuestStartNPCList } from "~/components/quest/QuestStartNPCList";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { PageTitle } from "~/components/ui/PageTitle";
import { questCategories } from "~/util/quest";
import { homePage } from "~/util/titles";
import { logPageView } from "~/util/stats";

function getQuests(gd: GameData, realm: Realm, arr: number[]) {
  const quests = arr.map((id) => selectQuestData(gd, realm, id));

  return quests.sort((a, b) => (a.minLevel ?? 0) - (b.minLevel ?? 0));
}

function selectQuestData(gd: GameData, realm: Realm, id: number) {
  const quest = gd.quests.get(id);
  invariant(quest);

  const t = tt.bind(null, realm.lang);

  return select(quest, {
    id: true,
    uname: true,
    name: t,
    desc: t,
    minLevel: true,
    maxLevel: true,
    reward: {
      exp: true,
      sp: true,
      items: { select: { itemName: true, icon: true, name: t } },
    },
    startNpcs: {
      select: {
        npcName: true,
        name: t,
        nick: t,
        nickColor: true,
        type: true,
        level: true,
      },
    },
    requirements: {
      races: { select: { raceName: true, icon: true, name: t } },
      occupation: { select: { profName: true, icon: true, name: t } },
    },
  });
}

// add starting quests
questCategories[0].categories.map((c) => {
  const path = `quests/${c.path}`;
  return $PAGE({
    path: path,
    loader: $LOADER?.(async (args) => {
      // Log
      logPageView(args);
      //
      return {
        quests: getQuests(args.gd, args.realm, c.quests),
        authUser: args.req.sess?.user,
      };
    }),
    meta: (cx) => {
      return {
        title: `${cx.t(c.name)} - ${cx.t({
          en: "Quests",
          ru: "Квесты",
        })} - ${homePage(cx.realm)}`,
        // description: getDescription({ realm: cx.realm, quest }),
        ogTitle: `All Quests ${cx.t(c.name)}`,
        canonicalUrl: `/quests/${c.path}`,
      };
    },
    render: ({ data }) => {
      const t = useT();
      const turl = useTurl();
      const title = t(c.name);

      return (
        <>
          <div className="content">
            <Breadcrumb
              items={[
                {
                  title: t({ en: "Quests", ru: "Квесты" }),
                  url: turl("/quests"),
                },
                { title, url: turl(`/${path}`) },
              ]}
            />
            <PageTitle>{title}</PageTitle>
          </div>
          <div style={{ overflowX: "auto" }}>
            <Table
              data={data.quests}
              id={(quest) => quest.id}
              cols={[
                {
                  title: t({ en: "Name", ru: "Имя" }),
                  cell: (quest) => (
                    <Link to={turl(`/quests/${quest.uname}`)}>
                      {quest.name}
                    </Link>
                  ),
                },
                {
                  title: t({ en: "Level", ru: "Уровень" }),
                  cell: (quest) => (
                    <>
                      {quest.minLevel || ""}
                      {quest.minLevel && quest.maxLevel ? "-" : null}
                      {quest.maxLevel || ""}
                    </>
                  ),
                },
                {
                  title: t({ en: "Reward", ru: "Награда" }),
                  cell: (quest) => <QuestRewardList reward={quest.reward} />,
                },
                {
                  title: t({ en: "Race", ru: "Раса" }),
                  cell: (quest) => (
                    <QuestRaceList races={quest.requirements.races} />
                  ),
                },
                {
                  title: t({ en: "Class", ru: "Класс" }),
                  cell: (quest) => (
                    <QuestProfList profs={quest.requirements.occupation} />
                  ),
                },
                {
                  title: t({ en: "Start NPC", ru: "Стартовый НПЦ" }),
                  cell: (quest) => (
                    <QuestStartNPCList startNpcs={quest.startNpcs} />
                  ),
                },
              ]}
            />
          </div>
        </>
      );
    },
  });
});

// add change class quests
questCategories[1].categories.map((c) => {
  const path = `quests/${c.path}`;
  return $PAGE({
    path: path,
    loader: $LOADER?.(async (args) => {
      return { quests: getQuests(args.gd, args.realm, c.quests) };
    }),
    meta: (cx) => {
      return {
        title: `${cx.t(c.name)} - ${cx.t({
          en: "Quests",
          ru: "Квесты",
        })} - ${homePage(cx.realm)}`,
        // description: getDescription({ realm: cx.realm, quest }),
        ogTitle: `All Quests ${cx.t(c.name)}`,
        canonicalUrl: `/quests/${c.path}`,
      };
    },
    render: ({ data }) => {
      const t = useT();
      const turl = useTurl();
      const title = t(c.name);

      return (
        <>
          <div className="content">
            <Breadcrumb
              items={[
                {
                  title: t({ en: "Quests", ru: "Квесты" }),
                  url: turl("/quests"),
                },
                { title, url: turl(`/${path}`) },
              ]}
            />
            <PageTitle>{title}</PageTitle>
          </div>
          <div style={{ overflowX: "auto" }}>
            <Table
              data={data.quests}
              id={(quest) => quest.id}
              cols={[
                {
                  title: t({ en: "Name", ru: "Имя" }),
                  cell: (quest) => (
                    <Link to={turl(`/quests/${quest.uname}`)}>
                      {quest.name}
                    </Link>
                  ),
                },
                {
                  title: t({ en: "Level", ru: "Уровень" }),
                  cell: (quest) => (
                    <>
                      {quest.minLevel || ""}
                      {quest.minLevel && quest.maxLevel ? "-" : null}
                      {quest.maxLevel || ""}
                    </>
                  ),
                },
                {
                  title: t({ en: "Reward", ru: "Награда" }),
                  cell: (quest) => <QuestRewardList reward={quest.reward} />,
                },
                {
                  title: t({ en: "Race", ru: "Раса" }),
                  cell: (quest) => (
                    <QuestRaceList races={quest.requirements.races} />
                  ),
                },
                {
                  title: t({ en: "Class", ru: "Класс" }),
                  cell: (quest) => (
                    <QuestProfList profs={quest.requirements.occupation} />
                  ),
                },
                {
                  title: t({ en: "Start NPC", ru: "Стартовый НПЦ" }),
                  cell: (quest) => (
                    <QuestStartNPCList startNpcs={quest.startNpcs} />
                  ),
                },
              ]}
            />
          </div>
        </>
      );
    },
  });
});
